"use client";

import { useAuthStore } from "@kaplan-labs/up-auth-api-client";

import { Upsell, useCart } from "@up/checkout";
import {
  ApplicationStatus,
  ProfileType,
  applicationIsDenied,
  applicationIsIncomplete,
  applicationIsPendingReview,
  useOwner,
  useOwnerProfile,
  useProfile,
  useTheme,
} from "@up/data";
import { ApplicationWarning, Auth, LoadingError, Toast, UPLink } from "@up/ui";

import { LineItem } from "./LineItem";
import { CartWrapper } from "./Wrapper";
import { isCartOpen } from "../is-cart-open-store";

import type { TContact, TProfileContext } from "@up/data";

const shouldShowEmptyCart = ({
  profile,
  ownerProfile,
}: {
  profile?: TProfileContext;
  ownerProfile?: TContact;
}) => {
  return (
    profile?.data?.type === ProfileType.Guardian &&
    (applicationIsIncomplete(ownerProfile?.application?.status) ||
      applicationIsDenied(ownerProfile?.application?.status) ||
      applicationIsPendingReview(ownerProfile?.application?.status))
  );
};

const CartEmpty = () => {
  return (
    <div className="border border-dashed border-gray-400 text-center py-12 text-gray-700">
      Cart is empty
    </div>
  );
};

export const Cart = function () {
  const auth = useAuthStore();
  const theme = useTheme();
  const cart = useCart();
  const owner = useOwner();
  const profile = useProfile();
  const ownerProfile = useOwnerProfile();
  const shoppingFor = (() => {
    const isGuardian = profile?.data?.type === ProfileType.Guardian;

    const isProxyShopping = owner?.email !== profile?.data?.email;
    if (isGuardian && isProxyShopping) {
      if (owner?.firstName && owner.lastName) {
        return `${owner.firstName} ${owner.lastName}`;
      } else {
        return owner?.email;
      }
    }
    return "";
  })();

  const showApplicationWarning =
    ownerProfile?.application?.status !== ApplicationStatus.Approved &&
    ownerProfile?.application?.status !== ApplicationStatus.AdminCompleted;

  const showCartDiscountMessage =
    Boolean(cart.data?.lineItems?.length) &&
    Boolean(cart.data?.cartDiscount?.heading) &&
    !cart.data?.coupon;

  if (!auth.currentUser) {
    return (
      <CartWrapper>
        <div className="flex flex-col gap-6 px-4 pt-px">
          <Auth />
        </div>
      </CartWrapper>
    );
  }

  return (
    <CartWrapper>
      {cart.didError ? (
        <div className="px-4 pt-3">
          <LoadingError error="Sorry, we could not load your cart" />
        </div>
      ) : (
        <div>
          {shoppingFor ? (
            <div className="px-6">Shopping for {shoppingFor}</div>
          ) : null}
          <br />
          {showApplicationWarning ? (
            <>
              <div className="px-6">
                <ApplicationWarning
                  appStatus={ownerProfile?.application?.status}
                  isStudent={profile?.data?.type !== ProfileType.Guardian}
                />
              </div>
              <br />
            </>
          ) : null}

          <div className="flex flex-col gap-6 px-4 pt-px">
            {cart.addItemError ? (
              <Toast variant="error">{cart.addItemError}</Toast>
            ) : null}

            {cart.data?.lineItems?.length ? (
              <ul className="flex flex-col gap-2">
                {cart.data?.lineItems.map((item) => {
                  return (
                    <LineItem
                      key={item._id}
                      item={item}
                      removeFn={async function (_id) {
                        return await cart.removeItem(_id);
                      }}
                    />
                  );
                })}
              </ul>
            ) : shouldShowEmptyCart({ profile, ownerProfile }) ? null : (
              <CartEmpty />
            )}

            {showCartDiscountMessage ? (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                <div className="font-bold">
                  {cart.data?.cartDiscount?.heading}
                </div>
                {cart.data?.cartDiscount?.message ? (
                  <div className="mt-1 opacity-70">
                    {cart.data.cartDiscount.message}
                  </div>
                ) : null}
              </div>
            ) : null}

            {cart.data?.lineItems?.length ? (
              <UPLink
                className={theme.buttonPrimaryClass}
                onClick={() => {
                  isCartOpen.set(false);
                }}
                href="/checkout"
              >
                Complete Enrollment
              </UPLink>
            ) : null}
            {!showApplicationWarning ? (
              <Upsell
                analytics={{
                  location: "cart-drawer",
                  lineItemCount: String(cart.data?.lineItems?.length ?? 0),
                }}
                showToggle={Boolean(cart.data?.lineItems?.length)}
                title={
                  cart.data?.lineItems?.length
                    ? "Interested in another course?"
                    : "Interested in adding a course?"
                }
              />
            ) : null}
          </div>
        </div>
      )}
    </CartWrapper>
  );
};
